import { useId } from 'react';
import { Tooltip } from 'react-tooltip';
import { VERIFICATION_DESCRIPTION, VERIFICATION_LABEL } from 'types/verification';
import { getProfileImageUrlOrPlaceholder } from 'utils/shared/user/getProfileImageUrlOrPlaceholder';
import AddPhoto from 'svg/AddPhoto';
import InfoCircle from 'svg/InfoCircle';
import Location from 'svg/Location';
import ShieldCheck from 'svg/ShieldCheck';
import ShieldCheckTrusted from 'svg/ShieldCheckTrusted';
import type { PlayerProfileSocialLink, PlayerProfileView } from 'screens/PlayerProfile/types';
import VerificationBadge from 'components/VerificationBadge';
import ProfileSocialLinks from '../ProfileSocialLinks';
import RoundIconButton from '../RoundIconButton';

interface Props {
  profile: PlayerProfileView;
  socialLinks: PlayerProfileSocialLink[];
  /** Object-URL of a just-picked file, shown until the refetched path arrives. */
  avatarPreviewUrl?: string | null;
  isViewerProfile: boolean;
  onEditAvatar?: () => void;
  isUploadingAvatar?: boolean;
  onEditSocialLinks?: () => void;
}

export default function ProfileHero({
  profile,
  socialLinks,
  avatarPreviewUrl,
  isViewerProfile,
  onEditAvatar,
  isUploadingAvatar = false,
  onEditSocialLinks,
}: Props) {
  const level = profile.verificationLevel;
  const LevelIcon = level === 'TRUSTED' ? ShieldCheckTrusted : ShieldCheck;
  // Namespaced so a second hero on the page can't hijack this tooltip's anchor.
  const tooltipId = `verification-${useId().replace(/:/g, '')}`;

  return (
    <div className="flex flex-col">
      {/* Avatar straddles the cover's bottom edge — centred on mobile, tucked into
          the bottom-left corner on desktop. */}
      <div className="-mt-[4.0625rem] flex justify-center md:-mt-[6.25rem] md:justify-start md:pl-ds-3xl">
        <div className="relative size-[8.125rem] md:size-[12.5rem]">
          <img
            src={
              avatarPreviewUrl ||
              getProfileImageUrlOrPlaceholder({ path: profile.profileImagePath })
            }
            alt={profile.name}
            className="size-full rounded-full border-4 border-color-bg-lightmode-primary object-cover dark:border-color-bg-darkmode-primary"
          />
          {isViewerProfile && onEditAvatar ? (
            <RoundIconButton
              label="Change profile photo"
              onClick={onEditAvatar}
              isLoading={isUploadingAvatar}
              icon={<AddPhoto className="h-4 w-4" />}
              className="absolute bottom-1 right-1"
            />
          ) : (
            <VerificationBadge level={level} className="size-7 md:hidden" />
          )}
        </div>
      </div>

      <div className="mt-ds-md flex flex-col items-center gap-ds-sm md:mt-ds-xl md:items-start md:gap-ds-md">
        <div className="contents md:flex md:flex-row md:items-center md:gap-ds-lg">
          <h1 className="typography-product-heading-mobile md:typography-product-heading-desktop order-1 text-center text-color-text-lightmode-primary dark:text-color-text-darkmode-primary md:text-left">
            {profile.name}
          </h1>

          {level && (
            <div className="typography-product-body-highlight order-3 flex items-center gap-ds-sm text-color-text-lightmode-primary dark:text-color-text-darkmode-primary md:order-2">
              <LevelIcon className="size-6 shrink-0 text-color-text-lightmode-primary dark:text-color-text-darkmode-primary md:size-8" />
              <span>{VERIFICATION_LABEL[level]}</span>

              <button
                type="button"
                aria-label={`What does ${VERIFICATION_LABEL[level]} mean?`}
                data-tooltip-id={tooltipId}
                data-tooltip-content={VERIFICATION_DESCRIPTION[level]}
                className="shrink-0 text-color-text-lightmode-icon dark:text-color-text-darkmode-icon"
              >
                <InfoCircle className="size-4 md:size-5" />
              </button>
              <Tooltip
                id={tooltipId}
                place="top"
                openOnClick
                // Above the sticky top nav; the default stacking context loses to it.
                className="!z-50 !max-w-[16rem] !rounded-lg !bg-color-bg-lightmode-invert !text-color-text-lightmode-invert"
              />
            </div>
          )}
        </div>

        <ProfileSocialLinks
          links={socialLinks}
          isViewerProfile={isViewerProfile}
          onEdit={onEditSocialLinks}
          className="order-4 md:order-2"
        />

        {profile.cityName && (
          <p className="typography-product-caption md:typography-product-body order-2 flex items-center gap-ds-xs text-color-text-lightmode-secondary dark:text-color-text-darkmode-secondary md:order-3">
            <img
              src="/images/app/pin-gradient.svg"
              className="h-6 w-auto shrink-0 text-color-text-brand md:h-4"
            />
            <span>{profile.cityName}</span>
          </p>
        )}
      </div>
    </div>
  );
}
